import { c as _c } from "react/compiler-runtime";
import { useQuery } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { activeCouponsAtom } from '@/atoms/promoAtom';
import { getCouponCandidates } from '@/utility/activeCoupons';
import { createRequestBatcher } from '@/utility/requestBatcher';
import { post } from '@/utility/requestHelpers/clientRequestHelpers';
import { Assortment, getShopIdForAssortment } from '@/utility/shopIds';
import { StrikePrice, StrikePriceCandidate, StrikePriceContext, buildStrikePriceRequest, parseStrikePriceResponse } from '@/utility/strikePriceValidation';
import { useCart } from './useCartHooks';
import { useCurrency } from './useCurrency';
import { useIncludePromoCoupons } from './useIncludePromoCoupons';
import { useIsMultiCouponEnabled } from './useIsMultiCouponEnabled';
import { useProductTypeCategoryIds } from './useProductTypeCategoryIds';
import { useShopIds } from './useShopIds';

// pricing-service accepts at most 100 baskets per validation request
const MAX_BASKETS_PER_REQUEST = 100;
// collects the strike prices of all items rendered together (e.g. a PLP page) into one request
const BATCH_WINDOW_MS = 10;
const FIVE_MINUTES = 5 * 60 * 1000;
type StrikePriceRequest = {
  contextKey: string;
  context: StrikePriceContext;
  candidate: StrikePriceCandidate;
};
const strikePriceBatcher = createRequestBatcher<StrikePriceRequest, StrikePrice | null>({
  windowMs: BATCH_WINDOW_MS,
  maxBatchSize: MAX_BASKETS_PER_REQUEST,
  getBatchKey: req => req.contextKey,
  loadBatch: async requests => {
    const {
      context
    } = requests[0]!;
    const request = buildStrikePriceRequest(context, requests.map(r => r.candidate));
    const prices = parseStrikePriceResponse(await post<unknown>('/api/v1/pricing/validation', request));
    return requests.map(r => prices.get(r.candidate.ref) ?? null);
  }
});

/**
 * Multi-coupon variant: the price the item would cost if it were added to the cart now, with all
 * active coupons validated by pricing-service — or `undefined` when no coupon lowers it (and in the
 * single-coupon control). Items rendered together are validated in one batched request; identical
 * items share one cache entry. Works on any page: categories come from the shop of the item's
 * `assortment` (default: marketplace); CYO items get no strike price while their shop is unknown.
 *
 * `price` is the item's unit price in major units, including its volume discount.
 */
export const useStrikePrice = t0 => {
  const $ = _c(38);
  const {
    price,
    productTypeId,
    hasVolumeDiscount,
    assortment: t1
  } = t0;
  const assortment = t1 === undefined ? "marketplace" : t1;
  const isMultiCouponEnabled = useIsMultiCouponEnabled();
  const includePromoCoupons = useIncludePromoCoupons();
  const shopIds = useShopIds();
  const {
    data: cartAndLineItemGroups,
    isSuccess: cartLoaded
  } = useCart();
  const {
    data: localeCurrency
  } = useCurrency();
  const activeCoupons = useAtomValue(activeCouponsAtom);
  const coupons = getCouponCandidates(cartAndLineItemGroups, activeCoupons, includePromoCoupons);
  let t2;
  if ($[0] !== assortment || $[1] !== shopIds) {
    t2 = getShopIdForAssortment(shopIds, assortment);
    $[0] = assortment;
    $[1] = shopIds;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const candidateShopId = t2;
  const currency = cartAndLineItemGroups?.cart.totalPrice.currencyCode ?? localeCurrency;
  const minorUnitPrice = Math.round(price * 100);
  const canValidate = isMultiCouponEnabled && cartLoaded && coupons.length > 0 && Boolean(productTypeId) && minorUnitPrice > 0 && Boolean(candidateShopId) && Boolean(currency);
  let t3;
  let t4;
  if ($[3] !== canValidate || $[4] !== candidateShopId || $[5] !== cartAndLineItemGroups?.lineItemGroups) {
    const cartShopIds = cartAndLineItemGroups?.lineItemGroups.map(_temp) ?? [];
    t3 = useProductTypeCategoryIds;
    t4 = canValidate ? [...cartShopIds, candidateShopId] : [];
    $[3] = canValidate;
    $[4] = candidateShopId;
    $[5] = cartAndLineItemGroups?.lineItemGroups;
    $[6] = t3;
    $[7] = t4;
  } else {
    t3 = $[6];
    t4 = $[7];
  }
  const {
    categoryIdsByShop,
    isSuccess: categoriesLoaded
  } = t3(t4);
  const t5 = cartAndLineItemGroups?.cart.id;
  const t6 = cartAndLineItemGroups?.cart.version;
  const t7 = coupons.join(",");
  let t8;
  if ($[8] !== candidateShopId || $[9] !== currency || $[10] !== t5 || $[11] !== t6 || $[12] !== t7) {
    t8 = [t5, t6, currency, candidateShopId, t7];
    $[8] = candidateShopId;
    $[9] = currency;
    $[10] = t5;
    $[11] = t6;
    $[12] = t7;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  const contextKey = t8.join("|");
  let t9;
  if ($[14] !== hasVolumeDiscount || $[15] !== minorUnitPrice || $[16] !== productTypeId) {
    t9 = [productTypeId, minorUnitPrice, hasVolumeDiscount];
    $[14] = hasVolumeDiscount;
    $[15] = minorUnitPrice;
    $[16] = productTypeId;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  const t10 = t9.join(":");
  const t11 = Number(productTypeId);
  let t12;
  if ($[18] !== hasVolumeDiscount || $[19] !== minorUnitPrice || $[20] !== t10 || $[21] !== t11) {
    t12 = {
      ref: t10,
      minorUnitPrice,
      productTypeId: t11,
      volumeDiscount: hasVolumeDiscount
    };
    $[18] = hasVolumeDiscount;
    $[19] = minorUnitPrice;
    $[20] = t10;
    $[21] = t11;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  const candidate = t12;
  let t13;
  if ($[23] !== candidate.ref || $[24] !== contextKey) {
    t13 = ["strikePrice", contextKey, candidate.ref];
    $[23] = candidate.ref;
    $[24] = contextKey;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] !== candidate || $[27] !== candidateShopId || $[28] !== cartAndLineItemGroups || $[29] !== categoryIdsByShop || $[30] !== contextKey || $[31] !== coupons || $[32] !== currency) {
    t14 = () => strikePriceBatcher.load({
      contextKey,
      candidate,
      context: {
        cartAndLineItemGroups,
        currency,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        coupons,
        categoryIdsByShop,
        candidateShopId
      }
    });
    $[26] = candidate;
    $[27] = candidateShopId;
    $[28] = cartAndLineItemGroups;
    $[29] = categoryIdsByShop;
    $[30] = contextKey;
    $[31] = coupons;
    $[32] = currency;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  const t15 = canValidate && categoriesLoaded;
  let t16;
  if ($[34] !== t13 || $[35] !== t14 || $[36] !== t15) {
    t16 = {
      queryKey: t13,
      queryFn: t14,
      enabled: t15,
      staleTime: FIVE_MINUTES
    };
    $[34] = t13;
    $[35] = t14;
    $[36] = t15;
    $[37] = t16;
  } else {
    t16 = $[37];
  }
  const {
    data: strikePrice
  } = useQuery(t16);
  return strikePrice && strikePrice.minorUnitItemPrice < strikePrice.minorUnitOriginalPrice ? strikePrice.minorUnitItemPrice / 100 : undefined;
};
function _temp(group) {
  return group.shopId;
}